import React, { useEffect, useState } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

interface FullReportGenerationProgressProps {
  /** Wall-clock start (ms) of the run, from useGenerateFullReport. */
  startedAt: number;
  /** Budget (ms) the run is given before the editor stops waiting. */
  timeoutMs: number;
}

/**
 * Blocking progress panel shown while a narrative-only report is being
 * expanded into the full report. Mirrors the ReviewPlan RevisionCountdown: a
 * determinate bar that drains toward the timeout, with a live mm:ss readout.
 * Rendered in place of the narrative so the report can't be edited or read
 * while the background run rebuilds it.
 */
export const FullReportGenerationProgress: React.FC<
  FullReportGenerationProgressProps
> = ({ startedAt, timeoutMs }) => {
  const [elapsed, setElapsed] = useState(() => Date.now() - startedAt);

  useEffect(() => {
    setElapsed(Date.now() - startedAt);
    const id = window.setInterval(() => {
      setElapsed(Date.now() - startedAt);
    }, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const remainingMs = Math.max(0, timeoutMs - elapsed);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  // Drain the bar toward the timeout, same direction as RevisionCountdown.
  // Guard the divisor: a non-positive timeoutMs would feed NaN/Infinity to
  // LinearProgress, so fall back to an empty bar.
  const progress = timeoutMs > 0 ? (remainingMs / timeoutMs) * 100 : 0;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: { xs: 3, md: 4 },
        bgcolor: "background.paper",
        textAlign: "center",
      }}
    >
      <Stack spacing={2} alignItems="center">
        <AutoAwesomeIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Generating the full report…
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            This runs in the background and may take a few minutes. The report
            is locked for editing until it finishes.
          </Typography>
        </Box>
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3 }}
            color="primary"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {minutes}:{seconds < 10 ? "0" : ""}
            {seconds} remaining
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
